/**
 * useFilterPersistence - Hook for persisting filter state to localStorage
 * 
 * Provides debounced localStorage persistence for filter configurations
 * with namespace support and error handling
 */
import { useState, useEffect, useCallback, useRef } from 'react';

interface UseFilterPersistenceOptions<T> {
  key: string;
  defaultFilters: T;
  debounceMs?: number;
}

interface UseFilterPersistenceReturn<T> {
  filters: T;
  setFilters: (filters: T | ((prev: T) => T)) => void;
  resetFilters: () => void;
  isLoading: boolean;
}

export function useFilterPersistence<T extends Record<string, any>>({
  key,
  defaultFilters,
  debounceMs = 500
}: UseFilterPersistenceOptions<T>): UseFilterPersistenceReturn<T> {
  const [filters, setFiltersState] = useState<T>(defaultFilters);
  const [isLoading, setIsLoading] = useState(true);
  const debounceRef = useRef<NodeJS.Timeout>();
  const isInitializedRef = useRef(false);

  // Load filters from localStorage on mount
  useEffect(() => {
    const loadFilters = () => {
      try {
        const stored = localStorage.getItem(`ping-filters-${key}`);
        if (stored) {
          const parsedFilters = JSON.parse(stored);
          
          // Merge with defaults to handle new filter properties
          const mergedFilters = {
            ...defaultFilters,
            ...parsedFilters
          };
          
          setFiltersState(mergedFilters);
        }
      } catch (error) {
        console.warn(`Failed to load filters for key "${key}":`, error);
        // Fall back to defaults on error
        setFiltersState(defaultFilters);
      } finally {
        setIsLoading(false);
        isInitializedRef.current = true;
      }
    };

    loadFilters();
  }, [key, defaultFilters]);

  // Debounced save to localStorage
  const saveToLocalStorage = useCallback((filtersToSave: T) => {
    // Clear existing timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Set new timeout
    debounceRef.current = setTimeout(() => {
      try {
        localStorage.setItem(`ping-filters-${key}`, JSON.stringify(filtersToSave));
      } catch (error) {
        console.warn(`Failed to save filters for key "${key}":`, error);
      }
    }, debounceMs);
  }, [key, debounceMs]);

  // Enhanced setFilters that handles both direct values and functions
  const setFilters = useCallback((newFilters: T | ((prev: T) => T)) => {
    setFiltersState(prevFilters => {
      const updatedFilters = typeof newFilters === 'function' 
        ? newFilters(prevFilters) 
        : newFilters;

      // Only save to localStorage after initial load
      if (isInitializedRef.current) {
        saveToLocalStorage(updatedFilters);
      }

      return updatedFilters;
    });
  }, [saveToLocalStorage]);

  // Reset filters to defaults
  const resetFilters = useCallback(() => {
    setFiltersState(defaultFilters);
    
    // Save reset state to localStorage
    if (isInitializedRef.current) {
      saveToLocalStorage(defaultFilters);
    }
  }, [defaultFilters, saveToLocalStorage]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return {
    filters,
    setFilters,
    resetFilters,
    isLoading
  };
}

export default useFilterPersistence;